import { useContext, useEffect, useMemo, useRef, useState, Suspense, lazy } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import type { PluggableList } from 'unified';
import Card from 'react-bootstrap/Card';
import { Button, Form, Spinner } from 'react-bootstrap';
import './style.scss';

import ElysiaBrainIcon from '../../assets/icon-elysia-brain.svg?react';
const CodeBlock = lazy(() => import('./CodeBlock'));
const TableCell = lazy(() => import('./TableCell'));
const ChartComponents = lazy(() => import('@/components/chat/ChartComponents'));

import LikeUnselectedIcon from '../../assets/icon-google-thumbsup-unselected.svg?react';
import DislikeUnselectedIcon from '../../assets/icon-google-thumbsdown-unselected.svg?react';
import CopyUnselectedIcon from '../../assets/icon-content copy-unselected.svg?react';
import TickIcon from '../../assets/icon-google-tick.svg?react';
import CloseIcon from '../../assets/icon-close.svg?react';

import ChatSources from '../common/ChatSources';
import { logCommentFeedback, logFeedback } from '../../services/feedback';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import InfoIcon from '../../assets/icon-info.svg?react';
import { SettingContext } from '@/context/SettingContext';
import IntermediateSteps from './IntermediateSteps';
import useElysiaStore from '@/app/elysia-store';
import ChatSystemFooterMessage from './ChatSystemFooterMessage';
import { getImageFromImageId, updateMetrics } from '@/services/aiApps';
import DownloadIcon from '@/assets/icon-download.svg?react';
import React from 'react';
import notify from '@/services/notify';
import { downloadPresentations } from '@/services/content';
import { triggerFileDownload } from '@/utils/downloadFile';
import Thinking from './Thinking';
import SQLSideBarModal from '../app-builder/SQLSideBarModal';
import GreatherThanIcon from '../../assets/icon-cheveron.svg?react';
import ContextFeild from '../common/UI/ContextFeild';
import { POWER_BI_LINK } from '@/config/settings';
import { CopyChart } from '@/utils/copyChart';
import { fixMarkdownTable } from '@/utils/chat';
import { authenticatedFetch } from '@/utils/authenticatedFetch';
import CitationBadge from '../common/CitationBadge';

const markdownRemarkPlugins: PluggableList = [[remarkGfm, { singleTilde: false }]];
const markdownRehypePlugins: PluggableList = [rehypeRaw];

const buildMarkdownComponents = (citationMap: Record<number, CitationGroup>) => ({
  table: (props: any) => (
    <Suspense fallback={<div>Loading table...</div>}>
      <div className="markdown-table-container overflow-x-auto pe-2">
        <table className="markdown-table" {...props} />
      </div>
    </Suspense>
  ),
  td: (props: any) => <TableCell {...props} />,
  th: (props: any) => <TableCell as="th" {...props} />,
  code: (props: any) => (
    <Suspense fallback={<div>Loading code block...</div>}>
      <CodeBlock {...props} />
    </Suspense>
  ),
  a: (props: any) => (
    <a href={props.href} className="custom-link" target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  ),
  'citation-badge': (props: any) => {
    const idx = Number(props?.['data-idx']);
    const group = citationMap?.[idx];
    if (!group) return null;
    return <CitationBadge group={group} />;
  },
});

const StreamingTextBlock = React.memo(
  function StreamingTextBlock({
    content,
    isStreaming,
    markdownComponents,
  }: {
    content: string;
    isStreaming: boolean;
    markdownComponents: ReturnType<typeof buildMarkdownComponents>;
  }) {
    const rendered =
      fixMarkdownTable(content) +
      (isStreaming && content?.length > 4 ? ' <span class="stream-cursor"></span>' : '');
    return (
      <Card.Text
        className={`sys-msg-card-desc overflow-x-auto ${isStreaming ? 'streaming-content' : ''}`}
        id="content-start"
      >
        <ReactMarkdown
          children={rendered}
          remarkPlugins={markdownRemarkPlugins}
          rehypePlugins={markdownRehypePlugins}
          components={markdownComponents}
        />
      </Card.Text>
    );
  },
  (prev, next) => prev.content === next.content && prev.isStreaming === next.isStreaming
);

StreamingTextBlock.displayName = 'StreamingTextBlock';

StreamingTextBlock.displayName = 'StreamingTextBlock';

const ChatSystemMessage = (props) => {
  const [feedback, setFeedback] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const badgeRef = useRef<HTMLDivElement>(null);
  const feedbackModalRef = useRef<HTMLDivElement>(null);
  const { selectedModel, selectedLanguage } = useContext(SettingContext);
  const markdownRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState('');
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [feedbackId, setFeedbackId] = useState<null | string>(null);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [showSQLPannel, setShowSQLPanel] = useState(false);

  const name = useElysiaStore((state) => state.name);

  const isPreview = useElysiaStore((state) => state.isPreview);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const activeChatHistoryRecord = useElysiaStore((state) => state.activeChatHistoryRecord);
  const complianceText = useElysiaStore((state) => state.complianceText);
  const appKey = useElysiaStore((state) => state.appKey);
  const setImageLoading = useElysiaStore.getState().setImageLoading;

  const isElysiaError = props?.message?.isElysiaError;
  const chartData = props?.message?.chartConfig;
  const imageLoading = props?.message?.imageLoading;
  const sqlSections = props?.message?.sqlSections;
  const responseBlocks = props?.message?.responseBlocks;
  const isStreaming = props?.message?.isStreaming;

  // Citation groups for this message, keyed by their `data-idx` so the
  // markdown component override can resolve inline markers to data.
  const citations: CitationGroup[] | undefined = props?.message?.citations;
  const citationMap = useMemo(() => {
    const map: Record<number, CitationGroup> = {};
    (citations || []).forEach((c) => {
      if (c) map[c.index] = c;
    });
    return map;
  }, [citations]);
  const markdownComponents = useMemo(() => buildMarkdownComponents(citationMap), [citationMap]);

  useEffect(() => {
    if (chartData) {
      setChartLoaded(true);
    }
  }, [chartData]);
  const chartRef = useRef(null);

  useEffect(() => {
    const value = props?.message?.feedback?.key || null;
    setFeedback(value);
  }, [props?.message?.feedback]);

  useEffect(() => {
    badgeRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [showBadge]);

  useEffect(() => {
    if (showFeedbackModal && feedbackModalRef.current) {
      feedbackModalRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showFeedbackModal]);

  //copy response function
  const handleCopyMessage = async () => {
    if (!markdownRef.current) return;

    try {
      if (image?.length > 0) {
        copyImageToClipboard();
      } else {
        let htmlContent = markdownRef.current.innerHTML;
        let plainText = markdownRef.current.innerText;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;

        const noCopyElements = tempDiv.querySelectorAll('.exclude-copy-response');
        if (noCopyElements.length > 0) {
          noCopyElements.forEach((element) => element.remove());

          htmlContent = tempDiv.innerHTML;
          plainText = tempDiv.innerText.trim().replace(/\n\s*\n\s*\n/g, '\n\n');
        }

        //chart
        if (chartLoaded && chartRef.current) {
          const chartInstance = chartRef.current as any;
          const canvas: HTMLCanvasElement | null =
            chartInstance.canvas ??
            chartInstance.canvasRef?.current ??
            chartInstance.ctx?.canvas ??
            null;

          if (canvas) {
            const success = await CopyChart(canvas, htmlContent, plainText);
            if (success) {
              setShowBadge(true);
              return;
            }
          }
        }

        // create a ClipboardItem with both HTML and plain text
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([clipboardItem]);
        setShowBadge(true);
      }
    } catch (error) {
      // fallback copy as plain text
      console.error(error);
      const plainText = markdownRef.current.innerText;
      await navigator.clipboard.writeText(plainText);
    } finally {
      const timeoutRef = setTimeout(() => {
        setShowBadge(false);
        clearTimeout(timeoutRef);
      }, 2000);
    }
  };

  const handleFeedback = async (e, runId, type) => {
    const agentName = image ? image : null;
    if (feedback === type) {
      setFeedback(null);
      if (runId) {
        const res = await logFeedback(runId, '', agentName);
        setFeedbackId(res?.feedback_id);
        await logCommentFeedback(props?.message?.runId, '', '', res?.feedback_id);
      }
    } else {
      setFeedback(type);
      setShowFeedbackModal(true);
      if (runId) {
        const res = await logFeedback(runId, type, agentName);
        setFeedbackId(res?.feedback_id);
      }
    }
  };

  const handleClose = () => {
    setShowFeedbackModal(false);
    setFeedbackText('');
  };

  const handleSubmitFeedback = async () => {
    const feedbackType = feedback === null ? '' : feedback;
    if (props?.message?.runId) {
      await logCommentFeedback(props?.message?.runId, feedbackType, feedbackText, feedbackId);
      handleClose();
    }
  };

  const toolCall = useMemo(() => {
    const tCall = props?.message?.toolCall;
    if (tCall) {
      if (chatAgent === 'Document') {
        return tCall !== 'Document' ? `Analyzing: ${tCall}` : tCall;
      } else if (chatAgent === 'app-agent') {
        return tCall;
      }
      return tCall !== 'Document' ? `Searching: ${tCall}` : tCall;
    }
    // eslint-disable-next-line
  }, [props?.message?.toolCall]);

  const toolResults = useMemo(() => {
    if (props?.message?.toolResult?.length) {
      return props?.message?.toolResult;
    }
    // eslint-disable-next-line
  }, [props?.message?.toolResult?.length]);

  useEffect(() => {
    setImage('');
    const getImage = async () => {
      setImageLoading(true);
      const res = await getImageFromImageId(props?.message?.imageId);
      setImage(res || '');
      setImageLoading(false);
    };
    if (props?.message?.imageId?.length > 0) {
      getImage();
    }
    return () => {
      setImage('');
    };
    // eslint-disable-next-line
  }, [props?.message?.imageId]);

  const downloadImage = async () => {
    if (!image) {
      console.error('No image content provided for download.');
      return;
    }

    try {
      const response = await authenticatedFetch(image);
      // Check if the response is OK
      if (!response.ok) {
        throw new Error(`Failed to fetch the blob. Status: ${response.status}`);
      }
      // Convert the response to a Blob
      const blob = await response.blob();
      triggerFileDownload(blob, 'image.jpg');
      updateMetrics(aiAppId, chatSessionId, 'image_download');
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const copyImageToClipboard = async () => {
    try {
      const response = await authenticatedFetch(image);
      if (response) {
        const blob = await response.blob();
        const clipboardItem = new ClipboardItem({
          [blob.type]: blob,
        });
        await navigator.clipboard.write([clipboardItem]);
        await updateMetrics(aiAppId, chatSessionId, 'image_copy');
      }
    } catch (error) {
      console.error('Failed to copy image:', error);
    }
  };

  const handleContextMenuClick = (e) => {
    e.preventDefault();
  };

  const handlePptDownload = async (presentationId, type) => {
    const res = await downloadPresentations(presentationId, type);
    const textContent = res?.data || '';
    notify({
      message: res?.data?.system_message || 'Download started',
      type: 'success',
    });
    const fileName = props?.message?.pptConfig?.ppt_name || props?.message?.pptConfig?.doc_name;
    triggerFileDownload(textContent, fileName);
  };

  // Reusable Image and Chart Rendering
  const mediaContent = (
    <>
      <div
        className="image-wrap"
        data-loading={appKey == 'image_generator' && isElysiaError == false}
      >
        {imageLoading && (
          <div className=" d-flex justify-content-center">
            <div className="image-skeleton">
              <Spinner />
            </div>
          </div>
        )}

        {image.length > 0 && !imageLoading && (
          <div className="image-section d-flex justify-content-center">
            <div className="image-container">
              <img
                src={image}
                alt="image"
                className="image-display"
                onClick={() => setIsViewerOpen(true)}
                onContextMenu={handleContextMenuClick}
              />
            </div>

            <div className="download-button-container">
              <Button
                className="download-button"
                onClick={downloadImage}
                aria-label="Download button"
              >
                <DownloadIcon />
              </Button>
            </div>
          </div>
        )}
      </div>
      {!imageLoading && complianceText?.length > 0 && !isElysiaError && (
        <Card.Text
          className="sys-msg-card-desc overflow-x-auto mt-2 image-description"
          ref={markdownRef}
        >
          <ReactMarkdown
            children={complianceText}
            remarkPlugins={markdownRemarkPlugins}
            rehypePlugins={markdownRehypePlugins}
            components={markdownComponents}
          />
          {isStreaming && <span className="stream-cursor" aria-hidden="true" />}
        </Card.Text>
      )}
      {isViewerOpen && (
        <div className="image-overlay">
          <button
            onClick={() => setIsViewerOpen(false)}
            className="close-button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          <img
            src={image}
            alt="Large view"
            className="large-image"
            onContextMenu={handleContextMenuClick}
          />
        </div>
      )}
    </>
  );

  // Reusable PPT Download Link
  const pptContent = (props?.message?.pptConfig?.ppt_name ||
    props?.message?.pptConfig?.doc_name) && (
    <p>
      Download :
      <span
        onClick={() =>
          handlePptDownload(
            props?.message?.pptConfig?.ppt_id || props?.message?.pptConfig?.doc_id,
            props?.message?.pptConfig?.ppt_id?.length > 0 ? 'presentations' : 'documents'
          )
        }
        className="ppt-title custom-link ms-1 text-decoration-underline"
        aria-label="Download PPT presentation"
      >
        {props?.message?.pptConfig?.ppt_name || props?.message?.pptConfig?.doc_name}
      </span>
    </p>
  );

  return (
    <>
      <div className="sys-msg-body mb-4">
        <Card className="sys-msg-card rounded response-card">
          <Card.Body className="custom-sys-card-body" data-testid="response-prompt-body">
            {((activeChatHistoryRecord?.metadata?.agent_name &&
              activeChatHistoryRecord?.metadata?.agent_name !== 'FinanceAgent') ||
              (name.length > 0 && name !== 'Finance Agent') ||
              chatAgent === 'Generic' ||
              chatAgent === 'Document' ||
              isElysiaError) && (
              <IntermediateSteps
                displayImage={appKey === 'tnf_sales_app' ? false : true}
                toolCall={toolCall}
                toolResults={toolResults}
                message={props?.message?.body}
                imageId={image}
              />
            )}

            <SQLSideBarModal
              show={showSQLPannel}
              setShow={setShowSQLPanel}
              sqlSections={sqlSections}
            />
            {responseBlocks && responseBlocks.length > 0 ? (
              <div ref={markdownRef} className="interleaved-content-wrapper">
                <Card.Text className="sys-msg-card-desc overflow-x-auto">{mediaContent}</Card.Text>
                {responseBlocks.map((block, index) => {
                  if (block.type === 'thinking') {
                    const isLastBlock = index === responseBlocks.length - 1;

                    return (
                      <Thinking
                        key={`think-${index}`}
                        thinkingText={block.content}
                        isLastBlockItem={isLastBlock}
                        remarkPlugins={markdownRemarkPlugins}
                        rehypePlugins={markdownRehypePlugins}
                        components={markdownComponents}
                        startedAt={block?.startedAt}
                        endedAt={block?.endedAt}
                      />
                    );
                  } else if (block.type === 'text') {
                    return (
                      <StreamingTextBlock
                        key={`text-${index}`}
                        content={block.content}
                        isStreaming={isStreaming}
                        markdownComponents={markdownComponents}
                      />
                    );
                  }
                  if (block.type === 'chartjs' && chartData) {
                    return (
                      <Suspense key={`chart-${index}`} fallback={<div>Loading Charts</div>}>
                        <ChartComponents chartConfig={chartData} chartRef={chartRef} />
                      </Suspense>
                    );
                  }
                  return null;
                })}
                <Card.Text className="sys-msg-card-desc overflow-x-auto">{pptContent}</Card.Text>
              </div>
            ) : (
              <>
                <Card.Text className="sys-msg-card-desc overflow-x-auto" ref={markdownRef}>
                  {mediaContent}
                  <>
                    {chartData && (
                      <>
                        <Suspense fallback={<div>Loading Charts</div>}>
                          <ChartComponents chartConfig={chartData} chartRef={chartRef} />
                        </Suspense>
                      </>
                    )}
                  </>
                  <ReactMarkdown
                    children={fixMarkdownTable(props?.message?.body)}
                    remarkPlugins={markdownRemarkPlugins}
                    rehypePlugins={markdownRehypePlugins}
                    components={markdownComponents}
                  />
                  {pptContent}
                  {isStreaming && <span className="stream-cursor" aria-hidden="true" />}
                </Card.Text>
              </>
            )}

            {appKey == 'finance' &&
              (props?.message?.showInteractions || props.historySystemMessage || isElysiaError) && (
                <div className="mb-3 feedback-line" data-testid="power-bi-link">
                  Standardised reporting available in &nbsp;
                  <a target="_blank" className="custom-link" href={POWER_BI_LINK} rel="noreferrer">
                    Power Informa
                  </a>
                </div>
              )}

            {appKey === 'finance' && (
              <div className="d-flex">
                <Button
                  onClick={() => setShowSQLPanel(true)}
                  className="sql-execution-btn"
                  variant="secondary"
                  data-testid="back-to-chat-button"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div>SQL Executions</div>
                    <GreatherThanIcon className="rightchevron-icon" />
                  </div>
                </Button>
              </div>
            )}
            {props?.message?.references && props?.message?.references.length > 0 && (
              <ChatSources references={props?.message?.references} />
            )}

            {appKey == 'finance' &&
              sqlSections?.length > 0 &&
              (props?.message?.showInteractions || props.historySystemMessage) && (
                <ContextFeild sqlSections={sqlSections} />
              )}
          </Card.Body>

          {(props?.message?.showInteractions || props.historySystemMessage) && (
            <div
              className={`sys-msg-card-interactions position-relative ${
                props?.message?.modelUsed || props?.message?.languageUsed
                  ? 'd-flex justify-content-between'
                  : ''
              } `}
            >
              {(props?.message?.modelUsed || props?.message?.languageUsed) && (
                <div>
                  <OverlayTooltip
                    tooltipText={`Response generated using ${
                      props?.message?.modelUsed ?? selectedModel?.display_name
                    } ${name !== 'Image Generator' ? 'model' : ''}  ${
                      props?.message?.languageUsed
                        ? ` and ${props?.message?.languageUsed ?? selectedLanguage} language`
                        : ''
                    }`}
                    buttonContent={<InfoIcon />}
                    buttonClassName={`unselected-chat-action-button me-1 like-button like-icon`}
                    ariaLabel="Model information"
                    testId="model-info"
                    tooltipClassName="tooltip-sidebar"
                    placement="right"
                  ></OverlayTooltip>
                  <Button
                    aria-label="like"
                    variant="none"
                    className={
                      feedback === 'thums_up'
                        ? 'chat-action-button me-1 like-button'
                        : 'unselected-chat-action-button me-1 like-button'
                    }
                    data-testid="like-button"
                    onClick={(e) => {
                      handleFeedback(e, props?.message?.runId, 'thums_up');
                    }}
                  >
                    <LikeUnselectedIcon
                      className={feedback === 'thums_up' ? 'like-icon' : 'unselected-like-icon'}
                    />
                  </Button>

                  <Button
                    aria-label="dislike"
                    variant="none"
                    className={
                      feedback === 'thums_down'
                        ? 'chat-action-button me-1 dislike-button'
                        : 'unselected-chat-action-button me-1 dislike-button'
                    }
                    data-testid="dislike-button"
                    onClick={(e) => {
                      handleFeedback(e, props?.message?.runId, 'thums_down');
                    }}
                  >
                    <DislikeUnselectedIcon
                      className={
                        feedback === 'thums_down' ? 'dislike-icon' : 'unselected-dislike-icon'
                      }
                    />
                  </Button>
                </div>
              )}
              {!isPreview && !isAppPreview && <ChatSystemFooterMessage />}
              <div className="text-end">
                <Button
                  aria-label="copy"
                  variant="none"
                  className="unselected-chat-action-button copy-button"
                  data-testid="copy-button"
                  onClick={handleCopyMessage}
                >
                  <CopyUnselectedIcon />
                </Button>
              </div>
              {showBadge && (
                <span ref={badgeRef} className="history-badge-for-copy-button position-absolute">
                  <TickIcon />
                  <span>Copied</span>
                </span>
              )}
            </div>
          )}
        </Card>

        {showFeedbackModal && (
          <div
            ref={feedbackModalRef}
            className="feedback-for-responses"
            data-testid="feedback-card"
          >
            <div className="feedback-header">
              <div className="feedback-text" data-testid="feedback-title">
                Feedback
              </div>
              <Button
                aria-label="close feedback"
                className="close-feedback"
                data-testid="close-feedback"
                onClick={() => {
                  handleClose();
                }}
              >
                <CloseIcon />
              </Button>
            </div>
            <div className="feedback-message">
              <ElysiaBrainIcon />
              <div className="feedback-content-text" data-testid="feedback-sub-title">
                Your feedback is much appreciated, why have you chosen this rating?
              </div>
            </div>
            <Form.Control
              className="inputForFeedback"
              placeholder="Additional feedback..."
              data-testid="feedback-text-field"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={feedbackText === ''}
              variant="secondary"
              className="submit-button"
              aria-label="submit feedback"
              data-testid="submit-feedback"
              onClick={() => {
                handleSubmitFeedback();
              }}
            >
              Submit
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(ChatSystemMessage);
